import { useCallback, useEffect, useRef, useState } from "react";
import { Modal, ModalConfig } from "@/components/Modal";
import { Stepper, StepKey } from "@/components/Stepper";
import { WhatsIcon } from "@/components/icons";
import { CollateralMode, CollateralScreen } from "@/screens/CollateralScreen";
import { ContractScreen } from "@/screens/ContractScreen";
import { LoginScreen } from "@/screens/LoginScreen";
import { PlanCard, PlansScreen } from "@/screens/PlansScreen";
import { ScheduleScreen } from "@/screens/ScheduleScreen";
import {
  CxReviewScreen,
  DoneChangeScreen,
  DoneKeepScreen,
  ErrorScreen,
  IneligibleScreen,
} from "@/screens/TerminalScreens";
import { config } from "@/config";
import {
  Availability,
  CollateralPlan,
  Offer,
  PaymentMethod,
  RiderInfo,
  RtoPath,
  StatusResponse,
  errorReason,
  errorStatus,
  fetchAvailability,
  fetchCollateralStatus,
  fetchContractHtml,
  fetchContractStatus,
  fetchEligibility,
  fetchStatus,
  postChoice,
  postCollateralLink,
  postConfirm,
  postContractSign,
  postSchedule,
} from "@/services/rto";
import { track } from "@/services/tracking";
import { clearAuthToken, loadAuthToken, saveAuthToken } from "@/utils/auth-token";

type Screen =
  | "loading"
  | "login"
  | "ineligible"
  | "plans"
  | "collateral"
  | "contract"
  | "schedule"
  | "done_keep"
  | "done_change"
  | "cx_review"
  | "error";

// Reads ?token=<jwt> from the URL (set by auth-core after WhatsApp login).
const captureTokenFromUrl = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");
  if (!token) return false;
  saveAuthToken(token);
  params.delete("token");
  const query = params.toString();
  window.history.replaceState(
    {},
    document.title,
    window.location.pathname + (query ? `?${query}` : "") + window.location.hash,
  );
  return true;
};

const redirectToAuth = (): void => {
  if (!config.authUrl) {
    console.error("VITE_AUTH_URL is not configured — cannot redirect to login.");
    return;
  }
  const url = new URL(config.authUrl);
  url.searchParams.set("redirectUrl", window.location.href);
  // Riders authenticate via WhatsApp OTP; auth-core's default is Google so
  // we have to opt in explicitly (same as lp-plan-migration — see
  // auth-core/INTEGRATION.md).
  url.searchParams.set("method", "whatsapp");
  window.location.href = url.toString();
};

const STEPS_KEEP: StepKey[] = ["plans", "collateral", "contract"];
const STEPS_CHANGE: StepKey[] = ["plans", "collateral", "contract", "schedule"];

export default function App() {
  const [screen, setScreen] = useState<Screen>("loading");
  const [modal, setModal] = useState<ModalConfig | null>(null);
  const [busy, setBusy] = useState(false);

  const [rider, setRider] = useState<RiderInfo | null>(null);
  const [offer, setOffer] = useState<Offer | null>(null);
  const [path, setPath] = useState<RtoPath | null>(null);
  const [collateralMode, setCollateralMode] = useState<CollateralMode | null>(null);
  const [payPendingWarn, setPayPendingWarn] = useState(false);
  const paymentUrl = useRef<string | null>(null);
  const [contractHtml, setContractHtml] = useState<string>("");
  const [contractSigned, setContractSigned] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [availability, setAvailability] = useState<Availability | null>(null);
  const [fullDayWarn, setFullDayWarn] = useState(false);
  const [booking, setBooking] = useState<{
    date: string;
    stationName: string;
    stationAddress: string | null;
  } | null>(null);
  const [effectiveTerm, setEffectiveTerm] = useState<number | null>(null);

  const show = useCallback((next: Screen) => {
    setScreen(next);
    try {
      window.scrollTo({ top: 0 });
    } catch {
      /* noop */
    }
  }, []);

  /* ------------------- flow steps ------------------- */

  const enterPlans = useCallback(async () => {
    const eligibility = await fetchEligibility();
    if (!eligibility.eligible || !eligibility.offer) {
      track("rto_lp_ineligible_view", { reason: eligibility.reason });
      show("ineligible");
      return;
    }
    setRider(eligibility.rider);
    setOffer(eligibility.offer);
    track("rto_offer_viewed", {
      segment: eligibility.offer.segment,
      paths: eligibility.offer.paths.map((p) => p.path),
    });
    show("plans");
  }, [show]);

  const enterContract = useCallback(
    async (currentPath: RtoPath) => {
      const [html, status] = await Promise.all([fetchContractHtml(), fetchContractStatus()]);
      setContractHtml(html);
      setContractSigned(status.signed);
      setPath(currentPath);
      setActionError(null);
      show("contract");
    },
    [show],
  );

  const enterSchedule = useCallback(async () => {
    const av = await fetchAvailability();
    setAvailability(av);
    setFullDayWarn(false);
    show("schedule");
  }, [show]);

  const stationInfo = useCallback(
    (stationId: number | null): { name: string; address: string | null } => {
      const st = availability?.stations.find((s) => s.station_id === stationId);
      return { name: st?.name ?? "Base Vammo", address: st?.address ?? null };
    },
    [availability],
  );

  // Server-authoritative resume (FR-4.3): /status decides the screen; the LP
  // never restarts a rider who already advanced.
  const resume = useCallback(async () => {
    show("loading");
    try {
      const status: StatusResponse = await fetchStatus();
      setRider(status.rider ?? null);
      setPath(status.path ?? null);
      setEffectiveTerm(status.effective_term_months ?? null);
      track("rto_lp_login", { eligible: status.state !== "ineligible", state: status.state });

      switch (status.state) {
        case "ineligible":
          track("rto_lp_ineligible_view", { reason: status.reason });
          show("ineligible");
          return;
        case "offer":
          await enterPlans();
          return;
        case "payment_pending":
          await resumePayment(status);
          return;
        case "signature_pending":
          await enterContract(status.path ?? "KEEP");
          return;
        case "scheduling_pending":
          await enterSchedule();
          return;
        case "scheduled":
          if (status.pickup_date) {
            const av = await fetchAvailability().catch(() => null);
            setAvailability(av);
            const st = av?.stations.find((s) => s.station_id === status.station_id);
            setBooking({
              date: status.pickup_date,
              stationName: st?.name ?? "Base Vammo",
              stationAddress: st?.address ?? null,
            });
            show("done_change");
            return;
          }
          await enterSchedule();
          return;
        case "cx_review":
          show("cx_review");
          return;
        case "completed":
          if (status.path === "CHANGE" && status.pickup_date) {
            setBooking({
              date: status.pickup_date,
              stationName: stationInfo(status.station_id ?? null).name,
              stationAddress: stationInfo(status.station_id ?? null).address,
            });
          }
          show("done_keep");
          return;
      }
    } catch (error) {
      if (!loadAuthToken()) {
        // 401 — token expired/invalid: back to the marketing screen.
        show("login");
        return;
      }
      console.error(error);
      show("error");
    }
  }, [enterContract, enterPlans, enterSchedule, show]);

  // payment_pending covers both "picked a plan card, never picked an option"
  // (payment_plan null) and "link generated, waiting for the webhook".
  const resumePayment = useCallback(
    async (status: StatusResponse) => {
      if (status.payment_plan) {
        const res = await postCollateralLink(status.payment_plan);
        if (res.state === "signature_pending") {
          await enterContract(status.path ?? "KEEP");
          return;
        }
        if (res.state === "cx_review") {
          show("cx_review");
          return;
        }
        paymentUrl.current = res.init_point;
        setCollateralMode({
          kind: "awaiting",
          initPoint: res.init_point,
          pix: res.pix,
          entryDue: res.entry_due,
          plan: status.payment_plan,
        });
        setPayPendingWarn(false);
        setPath(status.path ?? null);
        show("collateral");
        return;
      }
      const eligibility = await fetchEligibility();
      const offerPath = eligibility.offer?.paths.find((p) => p.path === status.path);
      if (!eligibility.offer || !offerPath) {
        show("error");
        return;
      }
      setOffer(eligibility.offer);
      setPath(status.path ?? null);
      setCollateralMode({ kind: "chooser", quote: offerPath.collateral });
      show("collateral");
    },
    [enterContract, show],
  );

  /* ------------------- boot ------------------- */

  useEffect(() => {
    captureTokenFromUrl();
    if (!loadAuthToken()) {
      show("login");
      return;
    }
    void resume();
  }, []);

  /* ------------------- actions ------------------- */

  const pickPlan = async (card: PlanCard) => {
    setBusy(true);
    try {
      await postChoice({ path: card.path.path, pricingModel: card.option.pricing_model });
      track("rto_plan_selected", {
        choice: card.path.path,
        pricing_model: card.option.pricing_model,
      });
      setPath(card.path.path);
      setCollateralMode({ kind: "chooser", quote: card.path.collateral });
      setPayPendingWarn(false);
      show("collateral");
    } catch (error) {
      // The record may have advanced in another tab — resync with the server.
      if (errorStatus(error) === 409) return void resume();
      console.error(error);
      show("error");
    } finally {
      setBusy(false);
    }
  };

  const generateLink = async (
    plan: CollateralPlan,
    method: PaymentMethod = "CHECKOUT",
    dueDate?: string,
  ) => {
    setBusy(true);
    try {
      const res = await postCollateralLink(plan, method, dueDate);
      track("rto_payment_link_generated", { plan, entry_due: res.entry_due, method, dueDate });
      if (res.state === "cx_review") {
        show("cx_review");
        return;
      }
      if (res.state === "signature_pending") {
        if (res.surplus > 0 && collateralMode?.kind === "chooser") {
          setCollateralMode({
            kind: "credit",
            paid: collateralMode.quote.paid,
            target: collateralMode.quote.upfront.target,
            surplus: res.surplus,
          });
          return;
        }
        await enterContract(path ?? "KEEP");
        return;
      }
      paymentUrl.current = res.init_point;
      // PIX renders on-page; only the hosted checkout opens another tab.
      if (method === "CHECKOUT" && res.init_point) window.open(res.init_point, "_blank", "noopener");
      setCollateralMode({
        kind: "awaiting",
        initPoint: res.init_point,
        pix: res.pix,
        entryDue: res.entry_due,
        plan,
        dueDate,
      });
      setPayPendingWarn(false);
    } catch (error) {
      if (errorStatus(error) === 409) return void resume();
      console.error(error);
      show("error");
    } finally {
      setBusy(false);
    }
  };

  const checkPaid = async () => {
    setBusy(true);
    try {
      const res = await fetchCollateralStatus();
      if (res.paid) {
        track("rto_collateral_paid", {});
        if (res.state === "cx_review") {
          show("cx_review");
          return;
        }
        await enterContract(path ?? "KEEP");
      } else {
        setPayPendingWarn(true);
      }
    } catch (error) {
      console.error(error);
      show("error");
    } finally {
      setBusy(false);
    }
  };

  const signContract = async (signatureDataUrl: string) => {
    setBusy(true);
    setActionError(null);
    try {
      await postContractSign(signatureDataUrl);
      track("rto_contract_signed", { path });
      setContractSigned(true);
      await afterSigned();
    } catch (error) {
      setActionError(errorReason(error) ?? "erro inesperado");
    } finally {
      setBusy(false);
    }
  };

  const afterSigned = async () => {
    if (path === "CHANGE") {
      // The signed→scheduling transition is applied lazily on status reads
      // (server-authoritative) — read it before booking or the schedule call
      // lands on a SIGNATURE_PENDING record.
      await fetchStatus();
      await enterSchedule();
      return;
    }
    // Keep path: execute the in-place migration now.
    try {
      const res = await postConfirm();
      track("rto_migration_completed", { path: "KEEP" });
      setEffectiveTerm(res.effective_term_months ?? null);
      show("done_keep");
    } catch (error) {
      // Paid + signed but the update failed (pending_retry) or a gate closed —
      // keep the rider on the contract screen with a retriable CTA.
      setActionError(errorReason(error) ?? "erro inesperado");
    }
  };

  const confirmSchedule = async (stationId: number, date: string) => {
    setBusy(true);
    setFullDayWarn(false);
    try {
      await postSchedule({ stationId, date });
      track("rto_swap_scheduled", { station_id: stationId, date });
      const info = stationInfo(stationId);
      setBooking({ date, stationName: info.name, stationAddress: info.address });
      show("done_change");
    } catch (error) {
      if (errorReason(error) === "day_fully_booked") {
        // Someone took the last slot — refresh the agenda instead of erroring.
        const av = await fetchAvailability().catch(() => null);
        if (av) setAvailability(av);
        setFullDayWarn(true);
        return;
      }
      if (errorStatus(error) === 409) return void resume();
      console.error(error);
      show("error");
    } finally {
      setBusy(false);
    }
  };

  /* ------------------- render ------------------- */

  const steps = path === "CHANGE" ? STEPS_CHANGE : STEPS_KEEP;
  const stepFor: Partial<Record<Screen, StepKey>> = {
    plans: "plans",
    collateral: "collateral",
    contract: "contract",
    schedule: "schedule",
  };
  const currentStep = stepFor[screen];
  const showHelp = !["loading", "login"].includes(screen);

  return (
    <div className="lp-conquista" data-state={screen}>
      {screen === "login" ? (
        <LoginScreen onEnter={redirectToAuth} />
      ) : (
        <div className="lpc-wrap">
          {currentStep ? <Stepper steps={steps} current={currentStep} /> : null}

          {screen === "loading" && <div className="lpc-spin" data-testid="spinner" />}
          {screen === "ineligible" && <IneligibleScreen />}
          {screen === "cx_review" && (
            <CxReviewScreen
              entryDue={
                collateralMode?.kind === "awaiting" ? collateralMode.entryDue : null
              }
              onPayNow={() => void resume()}
            />
          )}
          {screen === "error" && (
            <ErrorScreen
              onRetry={() => {
                if (!loadAuthToken()) {
                  clearAuthToken();
                  show("login");
                  return;
                }
                void resume();
              }}
            />
          )}
          {screen === "plans" && offer && (
            <PlansScreen
              offer={offer}
              onPick={(card) => void pickPlan(card)}
              onOptOut={() =>
                setModal({
                  title: "Sem problemas",
                  body: "Nenhuma mudança foi feita no seu plano. Se trocar de ideia no futuro, é só acessar a página novamente e solicitar a troca.",
                  confirmLabel: "Entendi",
                  cancelLabel: "Voltar pras opções",
                })
              }
            />
          )}
          {screen === "collateral" && collateralMode && (
            <CollateralScreen
              mode={collateralMode}
              pendingWarn={payPendingWarn}
              busy={busy}
              onBack={
                collateralMode.kind === "chooser" ? () => void enterPlans() : null
              }
              onGenerateLink={(plan, method, dueDate) =>
                void generateLink(plan, method, dueDate)
              }
              onReopenLink={() => {
                // Always regenerate: MP preferences expire in 30 minutes and an
                // expired checkout redirect-loops ("endereço inválido" no Safari).
                // Regeneration is idempotent server-side (same document, fresh
                // preference) — on the PIX view this same action switches to card.
                // Reuse the rider's already-chosen due date.
                if (collateralMode.kind === "awaiting")
                  void generateLink(collateralMode.plan, "CHECKOUT", collateralMode.dueDate);
              }}
              onCheckPaid={() => void checkPaid()}
              onCreditContinue={() => void enterContract(path ?? "KEEP")}
            />
          )}
          {screen === "contract" && (
            <ContractScreen
              contractHtml={contractHtml}
              alreadySigned={contractSigned}
              maskedEmail={rider?.masked_email ?? null}
              busy={busy}
              errorReason={actionError}
              openModal={setModal}
              onSign={(sig) => void signContract(sig)}
              onContinueSigned={() => {
                setBusy(true);
                void afterSigned().finally(() => setBusy(false));
              }}
            />
          )}
          {screen === "schedule" && availability && (
            <ScheduleScreen
              availability={availability}
              fullDayWarn={fullDayWarn}
              busy={busy}
              openModal={setModal}
              onConfirm={(stationId, date) => void confirmSchedule(stationId, date)}
            />
          )}
          {screen === "done_keep" && (
            <DoneKeepScreen
              firstName={rider?.first_name ?? null}
              effectiveTermMonths={effectiveTerm}
            />
          )}
          {screen === "done_change" && booking && (
            <DoneChangeScreen
              date={booking.date}
              stationName={booking.stationName}
              stationAddress={booking.stationAddress}
              onReschedule={() => void enterSchedule()}
            />
          )}

          {showHelp ? (
            <div className="lpc-help">
              <p>Está com dúvidas?</p>
              <a href="https://wa.me/+5511987826794" target="_blank" rel="noopener noreferrer">
                <WhatsIcon />
                Fale com nosso atendimento
              </a>
            </div>
          ) : null}
        </div>
      )}

      <Modal config={modal} onClose={() => setModal(null)} />
    </div>
  );
}
